import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BRAND_LABELS, type Brand, formatGoal } from "@/lib/marketing/constants";
import { GRADE_TEXT, type Grade } from "@/lib/marketing/grading";
import { cn } from "@/lib/utils";

interface InputCell {
  value: number;
  source: string;
}
interface KpiCell {
  value: number | null;
  grade: Grade;
}
interface PaidRow {
  month: string;
  hasData: boolean;
  inputs: Record<string, InputCell | null>;
  kpis: Record<string, KpiCell>;
}
interface Tile {
  label: string;
  value: string;
  grade: Grade;
  sub?: string;
}
interface PaidResponse {
  brand: Brand;
  fiscalYearLabel: string;
  editable: boolean;
  months: string[];
  inputs: { key: string; label: string }[];
  kpis: {
    key: string;
    label: string;
    unit: "currency" | "ratio" | "multiple";
    target?: number | null;
    dir?: "higher" | "lower";
  }[];
  rows: PaidRow[];
  tiles: Tile[];
}

const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
function fmtKpi(value: number | null, unit: "currency" | "ratio" | "multiple") {
  if (value === null) return "—";
  if (unit === "currency") return `$${value.toFixed(2)}`;
  if (unit === "ratio") return `${(value * 100).toFixed(1)}%`;
  return `${value.toFixed(2)}x`;
}
function fmtMonth(month: string) {
  const [y = 0, m = 1] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

function EditableCell({
  brand,
  month,
  metricKey,
  initial,
  onSaved,
}: {
  brand: Brand;
  month: string;
  metricKey: string;
  initial: number | null;
  onSaved: () => void;
}) {
  const [saved, setSaved] = useState(false);
  const save = async (raw: string) => {
    const trimmed = raw.trim();
    const value = trimmed === "" ? 0 : Number(trimmed);
    if (!Number.isFinite(value) || value === (initial ?? 0)) return;
    const res = await fetch("/api/marketing-suite/metric", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        periodType: "month",
        period: month,
        brand,
        platform: "paid",
        metricKey,
        value,
      }),
    });
    if (res.ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1200);
      onSaved();
    }
  };
  return (
    <input
      type="number"
      defaultValue={initial ?? ""}
      onBlur={(e) => save(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      className={cn(
        "w-20 rounded border bg-background px-1 py-0.5 text-right tabular-nums text-sm outline-none focus:border-brand-gold",
        saved && "border-green-500 ring-1 ring-green-500/30",
      )}
    />
  );
}

export function PaidDashboard({ brand }: { brand: Brand }) {
  const [data, setData] = useState<PaidResponse | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    fetch(`/api/marketing-suite/paid/${brand}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setError(true));
  }, [brand]);

  useEffect(() => {
    setData(null);
    setError(false);
    load();
  }, [load]);

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Couldn't load paid ads data.
        </CardContent>
      </Card>
    );
  }
  if (!data) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {data.tiles.map((t) => (
          <div key={t.label} className="rounded-lg border p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t.label}</p>
            <p className={cn("mt-1 text-2xl font-semibold tabular-nums", GRADE_TEXT[t.grade])}>
              {t.value}
            </p>
            {t.sub && <p className="mt-0.5 text-xs text-muted-foreground">{t.sub}</p>}
          </div>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {BRAND_LABELS[brand]} · Paid Ads — Monthly ({data.fiscalYearLabel})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-muted-foreground">
            ✏️ Manual entry — enter each month's totals from Meta Ads Manager
            {brand === "cb" ? " and Shopify" : ""}; KPIs calculate and grade automatically. Flips to
            auto once a Meta Ads connection is added.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="sticky left-0 bg-card text-left py-2 pr-4 font-medium text-muted-foreground">
                    Month
                  </th>
                  {data.inputs.map((i) => (
                    <th
                      key={i.key}
                      className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap"
                    >
                      {i.label}
                    </th>
                  ))}
                  <th className="w-2 border-l" aria-hidden />
                  {data.kpis.map((k) => {
                    const goal = formatGoal(
                      k.target ?? null,
                      k.unit === "multiple" ? "multiple" : k.unit,
                      k.dir ?? "higher",
                    );
                    return (
                      <th
                        key={k.key}
                        className="text-right py-2 px-2 font-medium text-muted-foreground whitespace-nowrap align-bottom"
                      >
                        <div>{k.label}</div>
                        {goal && (
                          <div className="text-[11px] font-normal text-brand-gold">🎯 {goal}</div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.month} className="border-b last:border-0">
                    <td className="sticky left-0 bg-card py-2 pr-4 font-medium whitespace-nowrap">
                      {fmtMonth(r.month)}
                    </td>
                    {data.inputs.map((i) => (
                      <td key={i.key} className="text-right py-1 px-1">
                        <EditableCell
                          brand={brand}
                          month={r.month}
                          metricKey={i.key}
                          initial={r.inputs[i.key]?.value ?? null}
                          onSaved={load}
                        />
                      </td>
                    ))}
                    <td className="border-l" aria-hidden />
                    {data.kpis.map((k) => {
                      const cell = r.kpis[k.key];
                      return (
                        <td
                          key={k.key}
                          className={cn(
                            "text-right py-2 px-2 tabular-nums font-medium",
                            r.hasData && cell ? GRADE_TEXT[cell.grade] : "text-muted-foreground",
                          )}
                        >
                          {r.hasData ? fmtKpi(cell?.value ?? null, k.unit) : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 font-semibold">
                  <td className="sticky left-0 bg-card py-2 pr-4">Total</td>
                  {data.inputs.map((i) => (
                    <td key={i.key} className="text-right py-2 px-2 tabular-nums">
                      {fmtInt(data.rows.reduce((s, r) => s + (r.inputs[i.key]?.value ?? 0), 0))}
                    </td>
                  ))}
                  <td className="border-l" aria-hidden />
                  <td
                    colSpan={data.kpis.length}
                    className="py-2 px-2 text-xs text-muted-foreground"
                  >
                    Colored vs benchmark: 🟢 on/above · 🟡 within 20% · 🔴 below
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
